import React, { useEffect, useState } from "react";
import { bidApi } from "../../lib/api";
import { ChevronDown, Pencil, Check, X } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid, ReferenceLine } from "recharts";

const eur = (n) =>
  new Intl.NumberFormat("nl-BE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Math.round(n || 0));

const monthLabel = (key) => {
  const [y, m] = key.split("-").map(Number);
  const s = new Date(y, m - 1, 1).toLocaleDateString("nl-BE", { month: "long", year: "numeric" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const profitCls = (n) => (n >= 0 ? "text-emerald-400" : "text-red-400");

export function ProfitOverview() {
  const [months, setMonths] = useState(null);
  const [open, setOpen] = useState(null);
  const [metric, setMetric] = useState("profit");
  const load = () => bidApi.profitOverview().then(setMonths).catch(() => setMonths([]));

  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!months) return null;
  const year = String(new Date().getFullYear());
  const ytd = months.filter((m) => m.month.startsWith(year));
  const sum = (arr, k) => arr.reduce((a, m) => a + (m[k] || 0), 0);
  const ytdProfitCount = sum(ytd, "count");

  return (
    <div data-testid="profit-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Kpi label={`Verkocht ${year}`} value={sum(ytd, "sold_count")} testid="kpi-sold" />
        <Kpi label={`Omzet ${year}`} value={eur(sum(ytd, "revenue"))} testid="kpi-revenue" />
        <Kpi label={`Winst ${year}`} value={eur(sum(ytd, "total_profit"))} accent testid="kpi-profit" />
        <Kpi label="Gem. winst per wagen" value={ytdProfitCount ? eur(sum(ytd, "total_profit") / ytdProfitCount) : "—"} testid="kpi-avg" />
      </div>
      {months.length === 0 ? (
        <div className="text-white/40 text-sm bg-[#242424] border border-white/5 rounded-lg px-4 py-4" data-testid="profit-empty">
          Nog geen verkochte wagens.
        </div>
      ) : (
        <>
          <div className="flex items-center gap-1 bg-[#1a1a1a] border border-white/10 rounded p-1 w-fit mb-3" data-testid="profit-metric-tabs">
            {[["profit", "Winst"], ["revenue", "Omzet"], ["sold_count", "Aantal verkocht"]].map(([k, label]) => (
              <button key={k} onClick={() => setMetric(k)} data-testid={`profit-metric-${k}`}
                className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded transition-colors ${metric === k ? "bg-[#c5a76f] text-black" : "text-white/50 hover:text-white"}`}>
                {label}
              </button>
            ))}
          </div>
          <ProfitChart months={months} metric={metric} />
          <div className="bg-[#242424] border border-white/5 rounded-lg divide-y divide-white/5">
            {months.map((m) => (
              <MonthRow key={m.month} m={m} open={open === m.month} onToggle={() => setOpen(open === m.month ? null : m.month)} onChanged={load} />
            ))}
          </div>
        </>
      )}
      <p className="text-[11px] text-white/35 mt-2">
        Omzet en aantal tellen alle wagens die als verkocht werden gezet. Winst telt enkel wagens met een gekoppeld bod en aankoopprijs: (echte verkoopprijs − aankoopprijs incl. veilingkost) / 1,21 − overige kosten. Is er geen echte verkoopprijs ingevuld, dan wordt de vraagprijs gebruikt (klik op de prijs om aan te passen).
      </p>
    </div>
  );
}

function Kpi({ label, value, accent = false, testid }) {
  return (
    <div className={`rounded-lg px-4 py-3 border ${accent ? "bg-[#c5a76f]/10 border-[#c5a76f]/40" : "bg-[#242424] border-white/5"}`} data-testid={testid}>
      <div className="text-[10px] tracking-widest uppercase text-white/40 mb-1">{label}</div>
      <div className={`text-xl font-bold ${accent ? "text-[#c5a76f]" : "text-white"}`}>{value}</div>
    </div>
  );
}

const shortMonth = (key) => {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("nl-BE", { month: "short", year: "2-digit" });
};

const METRIC = {
  profit: { name: "Winst", fmt: (v) => eur(v) },
  revenue: { name: "Omzet", fmt: (v) => eur(v) },
  sold_count: { name: "Verkocht", fmt: (v) => `${v} ${v === 1 ? "wagen" : "wagens"}` },
};

function ProfitChart({ months, metric }) {
  const data = [...months].reverse().map((m) => ({
    key: m.month, label: shortMonth(m.month), profit: Math.round(m.total_profit), revenue: Math.round(m.revenue || 0), sold_count: m.sold_count || 0, count: m.count,
  }));
  const money = metric !== "sold_count";
  return (
    <div className="bg-[#242424] border border-white/5 rounded-lg p-4 mb-3" data-testid="profit-chart">
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: "rgba(255,255,255,0.5)", fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tickFormatter={(v) => (money ? new Intl.NumberFormat("nl-BE", { style: "currency", currency: "EUR", notation: "compact", maximumFractionDigits: 1 }).format(v) : v)} tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 11 }} axisLine={false} tickLine={false} width={52} />
            <ReferenceLine y={0} stroke="rgba(255,255,255,0.2)" />
            <Tooltip
              cursor={{ fill: "rgba(197,167,111,0.08)" }}
              contentStyle={{ background: "#141414", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, fontSize: 12 }}
              labelStyle={{ color: "#c5a76f" }}
              itemStyle={{ color: "#fff" }}
              formatter={(v, _n, p) => [
                metric === "profit" ? `${eur(v)} (${p.payload.count} ${p.payload.count === 1 ? "wagen" : "wagens"} met bod)` : METRIC[metric].fmt(v),
                METRIC[metric].name,
              ]}
            />
            <Bar dataKey={metric} radius={[4, 4, 0, 0]} maxBarSize={56}>
              {data.map((d) => (
                <Cell key={d.key} fill={d[metric] >= 0 ? "#c5a76f" : "#f87171"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function PriceCell({ it, onChanged }) {
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(String(Math.round(it.selling_price)));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await bidApi.setSoldPrice(it.sold_id, val === "" ? null : Number(val));
      setEdit(false);
      onChanged();
    } catch (e) {
      window.alert(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setBusy(false);
    }
  };
  if (edit) {
    return (
      <span className="inline-flex items-center gap-1">
        <input type="number" min="0" value={val} onChange={(e) => setVal(e.target.value)} autoFocus onKeyDown={(e) => e.key === "Enter" && save()}
          className="w-24 bg-[#1a1a1a] border border-[#c5a76f]/40 rounded px-2 py-1 text-right text-white text-sm focus:outline-none" data-testid={`profit-price-input-${it.bid_id}`} />
        <button onClick={save} disabled={busy} className="p-1 text-emerald-400 hover:text-emerald-300" data-testid={`profit-price-save-${it.bid_id}`}><Check className="w-3.5 h-3.5" /></button>
        <button onClick={() => setEdit(false)} className="p-1 text-white/40 hover:text-white"><X className="w-3.5 h-3.5" /></button>
      </span>
    );
  }
  return (
    <button onClick={() => setEdit(true)} className="group inline-flex items-center gap-1.5 text-white/70 hover:text-white" data-testid={`profit-price-edit-${it.bid_id}`}
      title={it.real_price ? "Echte verkoopprijs — klik om aan te passen" : "Vraagprijs (geen echte verkoopprijs ingevuld) — klik om in te vullen"}>
      {eur(it.selling_price)}
      {!it.real_price && <span className="text-[9px] uppercase tracking-wider text-amber-400/80">vraagprijs</span>}
      <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-60 transition-opacity" />
    </button>
  );
}

function MonthRow({ m, open, onToggle, onChanged }) {
  return (
    <div data-testid={`profit-month-${m.month}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-4 px-4 py-3.5 hover:bg-white/5 transition-colors text-left" data-testid={`profit-month-toggle-${m.month}`}>
        <ChevronDown className={`w-4 h-4 text-white/40 transition-transform ${open ? "rotate-180" : ""}`} />
        <span className="text-white font-medium flex-1">{monthLabel(m.month)}</span>
        <span className="text-white/50 text-xs hidden sm:inline" data-testid={`profit-month-sales-${m.month}`}>
          {m.sold_count || 0} verkocht · omzet {eur(m.revenue)}
        </span>
        <span className="text-white/50 text-xs sm:hidden">{m.sold_count || 0} verkocht</span>
        <span className="text-white/35 text-xs hidden sm:inline">{m.count} met bod</span>
        <span className={`font-bold w-28 text-right ${profitCls(m.total_profit)}`} data-testid={`profit-month-total-${m.month}`}>{eur(m.total_profit)}</span>
      </button>
      {open && m.items.length === 0 && (
        <div className="px-4 pb-3 text-xs text-white/40">Geen verkochte wagens met een gekoppeld bod en aankoopprijs deze maand.</div>
      )}
      {open && m.items.length > 0 && (
        <div className="overflow-x-auto px-4 pb-3">
          <table className="w-full min-w-[600px] text-sm whitespace-nowrap">
            <thead>
              <tr className="text-white/35 text-[10px] uppercase tracking-wider">
                <th className="text-left py-2">Wagen</th>
                <th className="text-right py-2">Verkocht</th>
                <th className="text-right py-2">Verkoopprijs</th>
                <th className="text-right py-2">Aankoop</th>
                <th className="text-right py-2">Kosten</th>
                <th className="text-right py-2">Winst</th>
              </tr>
            </thead>
            <tbody>
              {m.items.map((it) => (
                <tr key={it.bid_id} className="border-t border-white/5" data-testid={`profit-item-${it.bid_id}`}>
                  <td className="py-2 text-white/85">
                    {it.title || "—"}
                    {it.number_plate && <span className="text-white/40 text-xs ml-2">{it.number_plate}</span>}
                  </td>
                  <td className="py-2 text-right text-white/50">{new Date(it.sold_at).toLocaleDateString("nl-BE")}</td>
                  <td className="py-2 text-right"><PriceCell it={it} onChanged={onChanged} /></td>
                  <td className="py-2 text-right text-white/70">{eur(it.purchase_price)}</td>
                  <td className="py-2 text-right text-white/50">{eur(it.costs)}</td>
                  <td className={`py-2 text-right font-semibold ${profitCls(it.profit)}`}>{eur(it.profit)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
